import { Inject, Injectable } from "@angular/core";
import { AppUrlsService } from "./app-urls.service";
import { AppEnvironmentConfig } from "../../environments/appEnvironmentConfig";
import { BRAND_CONFIG, BrandConfig } from "../shared/branding/brand-config";

@Injectable({ providedIn: "root" })
export class ClerkOptionsService {
  constructor(
    private readonly appUrlsService: AppUrlsService,
    @Inject(BRAND_CONFIG) private readonly brandConfig: BrandConfig,
  ) {}
  /**
   * Resolves the current Clerk satellite config based on the hostname where the application is currently
   * running.
   * @param config The application's current configuration.
   * @returns A set of options that can be used with Clerk.__init() to configre satellite domains.
   */
  public getSatelliteOptions(config: AppEnvironmentConfig): ClerkSatelliteOptions {
    if (config.urls.type === "static") {
      return {
        isSatellite: false,
        signInUrl: "/signIn",
        signUpUrl: "/signUp",
      };
    } else {
      const currentRoot = this.appUrlsService.currentRootDomain;
      if (!currentRoot) {
        throw new Error("Current hostname is not in the list of allowed root domains for this application.");
      }
      const primaryRoot = config.urls.primaryRootDomain;
      const isSatellite = currentRoot !== primaryRoot;
      const parsedWebsiteUrls = config.urls.websiteUrls.map((x) => new URL(x));
      const websiteUrl = parsedWebsiteUrls.find((x) => x.hostname === window.location.hostname);
      if (!websiteUrl) {
        throw new Error("Current hostname does not match any of the configured website URLs.");
      }

      const primaryWebsiteUrl = parsedWebsiteUrls.find((x) => x.hostname.endsWith(primaryRoot));
      if (!primaryWebsiteUrl) {
        throw new Error("Primary website URL not found for the primary root domain.");
      }
      const currentBrandId = this.brandConfig.brandId;
      return {
        signInUrl: isSatellite ? `${primaryWebsiteUrl.href}signIn?brand=${currentBrandId}` : "/signIn",
        signUpUrl: isSatellite ? `${primaryWebsiteUrl.href}signUp?brand=${currentBrandId}` : "/signUp",
        isSatellite,
        domain: isSatellite ? window.location.hostname : undefined,
        allowedRedirectOrigins: isSatellite
          ? undefined
          : [this.getFrontendApiUrl(config), ...parsedWebsiteUrls.map((x) => x.origin)],
      };
    }
  }

  /**
   * Extracts the Clerk Frontend API URL from the current environment's Clerk publishableKey.
   * @returns A URL in the form `https://someUrl.tld`.
   */
  private getFrontendApiUrl(config: AppEnvironmentConfig): string {
    // The frontend API URL is base64-encoded in the Clerk publishable key, as the last segment.
    const segments = config.clerk.publishableKey.split("_");
    if (segments.length !== 3) {
      throw new Error("Invalid Clerk publishable key.");
    }
    // Decode from base64, and remove the final '$' character that Clerk uses to indicate 'end of URL'.
    const url = window.atob(segments[segments.length - 1]).replace("$", "");
    return `https://${url}`;
  }
}

export interface ClerkSatelliteOptions {
  /**
   * The URL to redirect the user to for signing in.
   * On the primary domain, this should be a relative path to /signIn.
   * On the satellite domain, this should be an absolute path to the primary domain's /signIn page.
   */
  signInUrl: string;
  /**
   * The URL to redirect the user to for signin up.
   * On the primary domain, this should be a relative path to /signUp.
   * On the satellite domain, this should be an absolute path to the primary domain's /signUp page.
   */
  signUpUrl: string;
  /**
   * Whether or not this instance of the application is running on a satellite domain (e.g. not connect.rantalainen.com).
   */
  isSatellite: boolean;
  /**
   * The current application's domain, if running on a satellite domain.
   * Should be left unset if running on the primary domain.
   */
  domain?: string;
  /**
   * The list of domains that the primary domain is allowed to redirect to.
   * Should be a list of allowed satellite domains.
   * Should only be set on the application running on the primary domain.
   */
  allowedRedirectOrigins?: string[];
}
